import React, { useState, useMemo } from 'react';
import { Search, Filter, X, ChevronDown, Plus, Target, Check } from 'lucide-react';
import { DraggableClipCard } from './DraggableClipCard';
import { ClipPreviewPopup } from './ClipPreviewPopup';
import { TagBadge } from './ui';
import { CATEGORIES, TAGS, COLORS } from '@/data';
import type { Clip, ScheduledActivity, PickForUsItem } from '@/types';

interface ClipSidebarProps {
  clips: Clip[];
  scheduledActivities?: ScheduledActivity[];
  onScheduleViaModal?: () => void;
  onScheduleClip?: (clip: Clip) => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  // Pick For Us support
  pickForUsList?: PickForUsItem[];
  onAddToPickForUs?: (clip: Clip) => void;
  onRemoveFromPickForUs?: (clipId: string) => void;
  onSubmitPickForUs?: () => void;
  reviewerName?: string;
}

export const ClipSidebar: React.FC<ClipSidebarProps> = ({
  clips,
  scheduledActivities = [],
  onScheduleViaModal,
  onScheduleClip,
  isCollapsed = false,
  onToggleCollapse,
  pickForUsList = [],
  onAddToPickForUs,
  onRemoveFromPickForUs,
  onSubmitPickForUs,
  reviewerName,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilters, setCategoryFilters] = useState<string[]>([]);
  const [tagFilters, setTagFilters] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [previewClip, setPreviewClip] = useState<Clip | null>(null);

  // Get categories that have clips
  const activeCategories = useMemo(() => {
    const categoryIds = new Set(clips.map(c => c.categoryId));
    return CATEGORIES.filter(cat => categoryIds.has(cat.id));
  }, [clips]);

  // Get tags that actually exist on clips
  const activeTags = useMemo(() => {
    const tagIds = new Set<string>();
    clips.forEach(clip => {
      clip.tags?.forEach(tag => tagIds.add(tag));
    });
    return TAGS.filter(tag => tagIds.has(tag.id));
  }, [clips]);

  // Filter clips
  const filteredClips = useMemo(() => {
    return clips.filter(clip => {
      // Search term filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const category = CATEGORIES.find(c => c.id === clip.categoryId);
        const matchesSearch =
          clip.title.toLowerCase().includes(search) ||
          clip.description?.toLowerCase().includes(search) ||
          category?.name.toLowerCase().includes(search);
        if (!matchesSearch) return false;
      }

      // Category filters (any match if selected)
      if (categoryFilters.length > 0 && !categoryFilters.includes(clip.categoryId)) {
        return false;
      }

      // Tag filters (any match)
      if (tagFilters.length > 0) {
        const hasMatchingTag = tagFilters.some(tag => clip.tags?.includes(tag));
        if (!hasMatchingTag) return false;
      }

      return true;
    });
  }, [clips, searchTerm, categoryFilters, tagFilters]);

  const toggleCategoryFilter = (categoryId: string) => {
    setCategoryFilters(prev =>
      prev.includes(categoryId)
        ? prev.filter(c => c !== categoryId)
        : [...prev, categoryId]
    );
  };

  const toggleTagFilter = (tagId: string) => {
    setTagFilters(prev =>
      prev.includes(tagId)
        ? prev.filter(t => t !== tagId)
        : [...prev, tagId]
    );
  };

  const clearFilters = () => {
    setSearchTerm('');
    setCategoryFilters([]);
    setTagFilters([]);
  };

  const hasActiveFilters = searchTerm || categoryFilters.length > 0 || tagFilters.length > 0;
  const activeFilterCount = categoryFilters.length + tagFilters.length;

  if (isCollapsed) {
    return (
      <div className="w-12 flex-shrink-0">
        <button
          onClick={onToggleCollapse}
          className="w-full h-full bg-zinc-800/50 border border-zinc-700/50 rounded-xl p-2 flex flex-col items-center justify-start gap-2 hover:bg-zinc-700/50 transition-colors"
        >
          <Filter className="w-5 h-5 text-zinc-400" />
          <span
            className="text-xs font-medium writing-mode-vertical"
            style={{ writingMode: 'vertical-rl', color: COLORS.lime }}
          >
            Clips ({clips.length})
          </span>
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="w-72 flex-shrink-0 bg-zinc-800/50 border border-zinc-700/50 rounded-xl flex flex-col max-h-[calc(100vh-200px)]">
        {/* Header */}
        <div className="p-4 border-b border-zinc-700/50">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <Filter className="w-4 h-4" style={{ color: COLORS.lime }} />
              Clips ({filteredClips.length})
            </h3>
            {onToggleCollapse && (
              <button
                onClick={onToggleCollapse}
                className="p-1.5 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-700 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Search */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search clips..."
              className="w-full bg-zinc-700 border border-zinc-600 rounded-lg pl-10 pr-4 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-lime-500"
            />
          </div>

          {/* Filter toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-zinc-700/50 text-zinc-400 hover:text-white hover:bg-zinc-700 transition-colors text-sm"
          >
            <span className="flex items-center gap-2">
              <Filter className="w-4 h-4" />
              Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
            </span>
            <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>

          {/* Filters panel */}
          {showFilters && (
            <div className="mt-3 space-y-3">
              {/* Category filters - multiselect badges */}
              <div>
                <label className="block text-xs text-zinc-500 mb-1">Categories</label>
                <div className="flex flex-wrap gap-1">
                  {activeCategories.map(cat => {
                    const isSelected = categoryFilters.includes(cat.id);
                    const clipCount = clips.filter(c => c.categoryId === cat.id).length;
                    return (
                      <button
                        key={cat.id}
                        onClick={() => toggleCategoryFilter(cat.id)}
                        className={`px-2 py-1 rounded-lg text-xs font-medium transition-colors flex items-center gap-1 ${
                          isSelected
                            ? 'bg-lime-500/20 text-lime-400 border border-lime-500/50'
                            : 'bg-zinc-700/50 text-zinc-400 border border-zinc-600 hover:border-zinc-500'
                        }`}
                      >
                        <span>{cat.emoji}</span>
                        <span className="hidden sm:inline">{cat.name}</span>
                        <span className="text-zinc-500">({clipCount})</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Tag filters - only show tags that exist on clips */}
              {activeTags.length > 0 && (
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">Tags</label>
                  <div className="flex flex-wrap gap-1">
                    {activeTags.map(tag => (
                      <TagBadge
                        key={tag.id}
                        tag={tag.id}
                        selected={tagFilters.includes(tag.id)}
                        onClick={() => toggleTagFilter(tag.id)}
                        small
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Clear filters */}
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="w-full text-sm text-zinc-500 hover:text-zinc-300 transition-colors"
                >
                  Clear all filters
                </button>
              )}
            </div>
          )}
        </div>

        {/* Alternative: Schedule via modal button */}
        {onScheduleViaModal && (
          <div className="px-4 py-2 border-b border-zinc-700/50">
            <button
              onClick={onScheduleViaModal}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-zinc-900"
              style={{ backgroundColor: COLORS.lime }}
            >
              <Plus className="w-4 h-4" /> Schedule via Modal
            </button>
          </div>
        )}

        {/* Clips list */}
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {filteredClips.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-zinc-500 text-sm">No clips match your filters</p>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="mt-2 text-sm hover:underline"
                  style={{ color: COLORS.lime }}
                >
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            filteredClips.map(clip => {
              const isInPickList = pickForUsList.some(p => p.clipId === clip.id);
              return (
                <div key={clip.id} className="relative">
                  <DraggableClipCard
                    clip={clip}
                    onPreview={setPreviewClip}
                  />
                  {/* Pick For Us button overlay */}
                  {onAddToPickForUs && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isInPickList && onRemoveFromPickForUs) {
                          onRemoveFromPickForUs(clip.id);
                        } else {
                          onAddToPickForUs(clip);
                        }
                      }}
                      className={`absolute top-1 right-1 p-1.5 rounded-lg transition-colors ${
                        isInPickList
                          ? 'bg-purple-500 text-white'
                          : 'bg-zinc-700/80 text-zinc-400 hover:bg-purple-500/50 hover:text-purple-300'
                      }`}
                      title={isInPickList ? 'Remove from Pick For Us' : 'Add to Pick For Us'}
                    >
                      {isInPickList ? <Check className="w-3 h-3" /> : <Target className="w-3 h-3" />}
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Pick For Us Submit */}
        {pickForUsList.length > 0 && onSubmitPickForUs && (
          <div className="px-4 py-3 border-t border-purple-500/30 bg-purple-500/10">
            <p className="text-xs text-purple-300 mb-2 text-center">
              {pickForUsList.length} clip{pickForUsList.length !== 1 ? 's' : ''} selected
            </p>
            <button
              onClick={onSubmitPickForUs}
              className="w-full py-2 rounded-lg bg-purple-500 hover:bg-purple-400 text-white text-sm font-medium transition-colors flex items-center justify-center gap-2"
            >
              <Target className="w-4 h-4" />
              Send to {reviewerName || 'Reviewer'}
            </button>
          </div>
        )}

        {/* Hint */}
        <div className="px-4 py-3 border-t border-zinc-700/50 bg-zinc-900/30">
          <p className="text-xs text-zinc-500 text-center">
            {onAddToPickForUs
              ? `Click 🎯 to add clips for ${reviewerName || 'your reviewer'} to pick`
              : 'Click to preview • Drag onto calendar to schedule'
            }
          </p>
        </div>
      </div>

      {/* Preview Popup */}
      {previewClip && (
        <ClipPreviewPopup
          clip={previewClip}
          scheduledActivities={scheduledActivities}
          onClose={() => setPreviewClip(null)}
          onSchedule={onScheduleClip ? () => onScheduleClip(previewClip) : undefined}
        />
      )}
    </>
  );
};
